import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Check, ArrowRight, Star } from 'lucide-react';
import { PublicSection, Reveal } from '@/components/public';
import { PACKAGES } from '@/config/packages';
import HomeBusinessCard from './HomeBusinessCard';

/**
 * Homepage pricing — "Founding 50" cohort offer with 3 packages.
 * Prices come from the packages SSOT; setup copy + feature highlight follow
 * the founding-cohort positioning.
 */
const CARD_COPY = {
  core: {
    setup: '$199 One-Time Setup Fee',
    highlight: 'Tablet & Mobile Order Management',
  },
  pro: {
    setup: '50% Off Setup Fee ($99)',
    highlight: 'Integrated POS workflows',
  },
  growth: {
    setup: '$0 Setup Fee (Waived)',
    highlight: 'Featured Storefront Status',
  },
};

const scrollToForm = (e) => {
  e.preventDefault();
  document.getElementById('lead-form-anchor')?.scrollIntoView({ behavior: 'smooth' });
};

const TOTAL_SPOTS = 50;
const SPOTS_REMAINING = 25;
const CLAIMED_PCT = ((TOTAL_SPOTS - SPOTS_REMAINING) / TOTAL_SPOTS) * 100;

/**
 * FoundingCounter — static "spots remaining" display with an animated
 * claimed-progress bar. Renders the real SPOTS_REMAINING value from
 * first paint — no count-up animation — so SEO/AIEO crawlers (and
 * users on first render) never see a "0 / 50" state before the
 * counter has had time to animate.
 */
function FoundingCounter() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <div
      ref={ref}
      data-testid="founding-counter"
      className="shrink-0 w-full md:w-60 bg-background text-foreground border border-background/10 p-4"
    >
      <div className="flex items-baseline justify-center gap-1.5">
        <span
          data-testid="founding-spots-remaining"
          className="font-heading font-black text-5xl md:text-6xl leading-none tracking-tighter text-primary tabular-nums"
        >
          {SPOTS_REMAINING}
        </span>
        <span className="font-heading font-black text-2xl md:text-3xl leading-none tracking-tight text-muted-foreground tabular-nums">
          / {TOTAL_SPOTS}
        </span>
      </div>

      {/* claimed progress bar */}
      <div className="mt-3 h-2 w-full bg-muted overflow-hidden">
        <motion.div
          className="h-full bg-primary"
          initial={{ width: 0 }}
          animate={inView ? { width: `${CLAIMED_PCT}%` } : { width: 0 }}
          transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>

      <p className="mt-2.5 text-center text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
        Founding spots remaining
      </p>
    </div>
  );
}

export default function PricingPreviewSection() {
  return (
    <PublicSection
      id="pricing"
      testId="join-pricing-preview"
      variant="alt"
      align="center"
      padBottom="pb-10 md:pb-14"
      eyebrow="Packages"
      title="One flat monthly fee. 0% commission on food sales."
      description="Month-to-month plans built around what your business needs — not a cut of every order."
    >
      {/* Founding 50 banner */}
      <Reveal>
        <div
          data-testid="founding-50-banner"
          className="max-w-3xl mx-auto mb-12 md:mb-14 bg-foreground text-background p-6 md:p-8"
        >
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
            <div className="flex-1 text-center md:text-left">
              <p className="inline-flex items-center gap-2 font-heading font-black text-lg md:text-xl tracking-tight justify-center md:justify-start">
                <Star className="w-5 h-5 text-primary fill-primary" />
                Join the Founding 50 Cohort
              </p>
              <p className="mt-2 text-sm md:text-base text-background/70 leading-relaxed">
                We are currently onboarding a select group of independent Alberta merchants.
                Secure your spot to lock in priority onboarding.
              </p>
            </div>
            <FoundingCounter />
          </div>
        </div>
      </Reveal>

      {/* Pricing cards */}
      <div className="grid gap-6 md:grid-cols-3 items-stretch text-left">
        {PACKAGES.map((pkg, i) => {
          const copy = CARD_COPY[pkg.id];
          const isPopular = pkg.id === 'pro';
          return (
            <Reveal key={pkg.id} delay={i * 0.06} className="h-full">
              <div
                data-testid={`pricing-card-${pkg.id}`}
                className={`relative flex flex-col h-full bg-background p-6 md:p-8 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 ${
                  isPopular
                    ? 'border-2 border-primary shadow-[0_0_50px_-14px_hsl(var(--primary)/0.5)] md:-mt-4 md:mb-4'
                    : 'border border-border hover:shadow-[0_0_40px_-16px_hsl(var(--foreground)/0.25)]'
                }`}
              >
                {isPopular && (
                  <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[10px] font-bold px-4 py-1.5 uppercase tracking-[0.15em] whitespace-nowrap">
                    Most Popular
                  </span>
                )}

                <p className="font-heading font-black text-xl tracking-tight text-foreground">
                  {pkg.name}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">{pkg.tagline}</p>

                <p className="mt-6 font-heading font-black text-5xl tracking-tighter text-foreground leading-none">
                  {pkg.priceLabel}
                  <span className="text-base text-muted-foreground font-medium">{pkg.sub}</span>
                </p>
                <p className="mt-2 text-sm font-semibold text-primary">{copy.setup}</p>

                {/* Feature highlight */}
                <div className="mt-6 bg-muted/60 border border-border px-4 py-3">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
                    Feature highlight
                  </p>
                  <p className="text-sm font-semibold text-foreground">{copy.highlight}</p>
                </div>

                <ul className="mt-6 space-y-2.5 flex-1">
                  {pkg.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                      <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" strokeWidth={2.5} /> {h}
                    </li>
                  ))}
                </ul>

                <a
                  href="#lead-form-anchor"
                  onClick={scrollToForm}
                  data-testid={`pricing-cta-${pkg.id}`}
                  className={`mt-7 inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full text-sm font-semibold transition-[background-color,transform,color] duration-200 hover:-translate-y-0.5 ${
                    isPopular
                      ? 'bg-primary hover:bg-primary-hover text-primary-foreground'
                      : 'border border-foreground/30 text-foreground hover:bg-foreground hover:text-background'
                  }`}
                >
                  Start with {pkg.name} <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </Reveal>
          );
        })}
      </div>

      {/* Home Business — full-width card spanning the width of the 3-tier grid */}
      <div className="mt-6 md:mt-8">
        <HomeBusinessCard testIdPrefix="pricing-home-business" />
      </div>

      <Reveal delay={0.12}>
        <div className="mt-10 text-center">
          <Link
            to="/features"
            data-testid="pricing-see-full-comparison"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
          >
            See the full feature comparison <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </Reveal>
    </PublicSection>
  );
}
